import {
  Alert,
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { toast } from 'react-toastify';

import { caloTheme } from '../../assets/themes/calo';
import ConfirmationMessage from '../../components/ConfirmationMessage';
import { useManualBatch } from '../../hooks/useBatchTrackingMutations';
import { useTrackableItems } from '../../hooks/useBatchTrackingQueries';
import type { Kitchen } from '../../libs';
import { useDocumentMedia } from '../../libs/utils/document';
import { manualBatchErrorMessage, refusedByBatchState } from '../BatchTracking/errors';
import { formatQuantity } from '../BatchTracking/format';
import type { Batch, ItemKind, TrackableItem } from '../BatchTracking/types';
import { chefText, outlineButtonSx, primaryButtonSx, quietLinkSx } from '../chefStyles';
import { minutesLeft } from './editWindow';
import { fieldsOf, parseQuantity, problemMessage, unitChoices } from './quantity';

const { palette } = caloTheme;

const MOST_ITEMS_SHOWN = 50;
const KIND_LABEL: Record<ItemKind, string> = { component: 'Component', ingredient: 'Ingredient' };

const fieldSx = {
  '& .MuiInputBase-input': { fontSize: { xs: 16, md: 18 } },
  '& .MuiFormHelperText-root': {
    mx: 0,
    mt: 0.75,
    fontSize: { xs: 14, md: 16 },
    lineHeight: { xs: '20px', md: '24px' }
  }
};

const optionSx = { '& .MuiAutocomplete-option': { minHeight: 56, py: 1.5 } };

// The chosen unit reads as the chef buttons' answer does: brand text and edge on the light brand fill.
const unitSx = {
  minWidth: 64,
  px: 2,
  fontSize: { xs: 16, md: 18 },
  fontWeight: 500,
  textTransform: 'none',
  '&:hover': { backgroundColor: palette.neutral50 },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: palette.primary100,
    color: palette.primary800,
    borderColor: palette.primary800
  }
} as const;

const deleteSx = {
  color: palette.red,
  border: `1px solid ${palette.red}`,
  '&:hover': { backgroundColor: palette.neutral50, border: `1px solid ${palette.red}` }
};

const filterItems = createFilterOptions<TrackableItem>({ ignoreAccents: true, trim: true, limit: MOST_ITEMS_SHOWN });
const byName = (a: TrackableItem, b: TrackableItem): number => a.name.localeCompare(b.name);

type ManualBatchDialogProps = {
  kitchen: Kitchen;
  /** The batch to change; without it the dialog records a new one. */
  batch?: Batch;
  now: Date;
  onClose: () => void;
  /** The server refused a change because of the batch's state: Home reads its batches again. */
  onRefused: () => void;
};

const ManualBatchDialog = ({ kitchen, batch, now, onClose, onRefused }: ManualBatchDialogProps) => {
  const { isMobile } = useDocumentMedia();
  const catalog = useTrackableItems(kitchen);
  const { create, update, remove, loading: saving } = useManualBatch(kitchen);
  const items = useMemo(() => catalog.items.toSorted(byName), [catalog.items]);

  const [picked, setPicked] = useState<TrackableItem | null>(null);
  const [text, setText] = useState(() => (batch ? fieldsOf(batch.quantity, batch.unit).text : ''));
  const [chosenUnit, setChosenUnit] = useState(() => (batch ? fieldsOf(batch.quantity, batch.unit).choice.label : undefined));
  const [attempted, setAttempted] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // An account the server refuses for the kitchen gets no dialog and no explanation; Home reads again and hides Add.
  useEffect(() => {
    if (catalog.unauthorized) {
      onRefused();
      onClose();
    }
  }, [catalog.unauthorized, onRefused, onClose]);

  const item = picked ?? items.find(({ id }) => id === batch?.itemId) ?? null;
  const choices = item === null ? undefined : unitChoices(item.unit);
  const choice = choices?.find(({ label }) => label === chosenUnit) ?? choices?.[0];
  const quantity = choice === undefined ? undefined : parseQuantity(text, choice);
  const problem = quantity !== undefined && 'problem' in quantity ? quantity.problem : undefined;
  const quantityProblem =
    attempted && problem !== undefined && choice !== undefined ? problemMessage(problem, choice) : undefined;
  const left = batch === undefined ? undefined : minutesLeft(batch, now);

  const attempt = async (change: () => Promise<unknown>) => {
    setFailure(undefined);
    try {
      await change();
      onClose();
    } catch (error) {
      if (refusedByBatchState(error)) {
        toast.error(manualBatchErrorMessage(error));
        onRefused();
        onClose();
      } else {
        setFailure(manualBatchErrorMessage(error));
      }
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (item === null || quantity === undefined || !('quantity' in quantity)) {
      return;
    }
    const input = { itemType: item.kind, itemId: item.id, quantity: quantity.quantity };
    await attempt(() => (batch ? update(batch.id, input) : create(input)));
  };

  const edge = isMobile ? 2 : 3;
  return (
    <Dialog
      open
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      aria-labelledby="manual-batch-title"
      slotProps={{ paper: { sx: isMobile ? {} : { borderRadius: '16px' } } }}
    >
      <DialogTitle
        id="manual-batch-title"
        sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 700, color: palette.neutral900, px: edge, pt: edge, pb: 1 }}
      >
        {batch ? 'Change batch' : 'Manual batch'}
      </DialogTitle>
      <DialogContent sx={{ px: edge }}>
        <Box
          component="form"
          id="manual-batch-form"
          noValidate
          onSubmit={(event) => void save(event)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
        >
          {left !== undefined && (
            <Typography sx={{ ...chefText.body, color: palette.neutral800 }}>
              {left > 0 ? `${left} min left to change or delete this batch.` : 'The time to change this batch is over.'}
            </Typography>
          )}
          {catalog.error && !catalog.unauthorized && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={catalog.reload}>
                  Try again
                </Button>
              }
            >
              Couldn't load the items.
            </Alert>
          )}
          <Autocomplete
            options={items}
            value={item}
            loading={catalog.loading}
            getOptionLabel={({ name }) => name}
            isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
            filterOptions={filterItems}
            onChange={(_, next) => setPicked(next)}
            slotProps={{ listbox: { sx: optionSx } }}
            renderOption={({ key, ...props }, option) => (
              <li key={key} {...props}>
                <Box>
                  <Typography sx={chefText.body}>{option.name}</Typography>
                  <Typography sx={{ ...chefText.meta, color: palette.neutral800 }}>{KIND_LABEL[option.kind]}</Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item"
                placeholder="Search components and ingredients"
                sx={fieldSx}
                error={attempted && item === null}
                helperText={attempted && item === null ? 'Pick an item.' : item === null ? undefined : KIND_LABEL[item.kind]}
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              label="Quantity"
              value={text}
              disabled={choices === undefined}
              error={quantityProblem !== undefined}
              helperText={quantityProblem ?? (choices === undefined ? 'Pick an item first.' : undefined)}
              autoComplete="off"
              sx={{ flex: 1, ...fieldSx }}
              onChange={(event) => setText(event.target.value)}
              slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input:
                  choices?.length === 1
                    ? { endAdornment: <InputAdornment position="end">{choices[0].label}</InputAdornment> }
                    : {}
              }}
            />
            {choices !== undefined && choices.length > 1 && (
              <ToggleButtonGroup
                exclusive
                aria-label="Unit"
                value={choice?.label}
                sx={{ height: 56 }}
                onChange={(_, next: string | null) => next !== null && setChosenUnit(next)}
              >
                {choices.map(({ label }) => (
                  <ToggleButton key={label} value={label} sx={unitSx}>
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Box>
          {failure !== undefined && (
            <Alert severity="error" role="alert">
              {failure}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 1.5,
          px: edge,
          pt: 2,
          pb: isMobile ? 'calc(16px + env(safe-area-inset-bottom))' : 3,
          '& .MuiButton-root': isMobile ? { width: '100%' } : {}
        }}
      >
        {batch && (
          <Button
            variant="outlined"
            sx={{ ...outlineButtonSx, ...deleteSx, ...(isMobile ? { mb: 1 } : { mr: 'auto' }) }}
            disabled={saving || left === 0}
            onClick={() => setConfirmingDelete(true)}
          >
            Delete
          </Button>
        )}
        <Button variant="text" sx={quietLinkSx} onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" form="manual-batch-form" variant="contained" sx={primaryButtonSx} disabled={saving || left === 0}>
          Save
        </Button>
      </DialogActions>
      {batch && (
        <ConfirmationMessage
          isOpened={confirmingDelete}
          handleClose={() => setConfirmingDelete(false)}
          handleAccept={() => void attempt(() => remove(batch.id))}
          message={`Delete ${batch.itemName}, ${formatQuantity(batch.quantity, batch.unit)}?\nThis can't be undone.`}
          accept="Delete"
          reject="Cancel"
          messageFontSize="18px"
        />
      )}
    </Dialog>
  );
};

export default ManualBatchDialog;
